import { SharedModule } from 'src/app/shared/shared.module';
import { CoreModule } from './core/core.module';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateService } from '@ngx-translate/core';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { TranslateModule } from '@ngx-translate/core';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { AppConfigService } from './core/service/app-config.service';
import { AuthInterceptorService } from './core/service/interceptor/auth-interceptor.service';
import { ApiPrefixInterceptorService } from './core/service/interceptor/api-prefix-interceptor.service';
import { ErrorInterceptorService } from './core/service/interceptor/error-interceptor.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { GlobalErrorHandler } from './core/error-handler/error-handler';
import { EditorModule } from '@progress/kendo-angular-editor';
import { KendoMessageService } from './core/service/kendo-message.service';
import { MessageService } from '@progress/kendo-angular-l10n';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { ICON_SETTINGS, IconSettingsService } from '@progress/kendo-angular-icons';
import { IconService } from './core/service/icon.service';
import { IntercomModule } from 'ng-intercom-x';
import { ChannelsChatModule } from './chat/chat.module';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';

if (environment.production)
{
  enableProdMode();
}

// Application initializer
const appConfigInitializerFn = (appConfig: AppConfigService) => () => appConfig.loadAppConfig();
const getBaseHref = (platformLocation: PlatformLocation): string => platformLocation.getBaseHrefFromDOM();

// Custom handler to provide log when missing translation is found
export class CustomTranslationHandler implements MissingTranslationHandler
{
  public handle(params: MissingTranslationHandlerParams): string
  {
    console.warn('Missing Translation for key = ' + params.key);
    return params.key;
  }
}

// Function to initialize the application and wait for translations (This part is correct)
export function appTranslateInitializerFactory(translate: TranslateService)
{
  return () =>
  {
    const defaultLang = 'en';
    translate.setFallbackLang(defaultLang);
    return new Promise<void>((resolve) =>
    {
      translate.use(defaultLang).subscribe({
        next: () => { resolve() },
        error: (err) =>
        {
          console.error(`TranslateService: Error loading initial language '${defaultLang}'.`, err);
          resolve()
        }
      });
    });
  };
}

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.ERROR : NgxLoggerLevel.TRACE
    }),
    TranslateModule.forRoot({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/', suffix: '.json',
        enforceLoading: true,   // Adds cache-busting timestamp
        useHttpBackend: true    // Bypasses HTTP interceptors
      }),
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomTranslationHandler },
      fallbackLang: 'en'
    }),
    BrowserAnimationsModule,
    environment.workerEnabled ? ServiceWorkerModule.register('./sw.js', { enabled: true, registrationStrategy: 'registerImmediately', scope: '/channels/' }) : [],
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-center',
      closeButton: true,
      progressBar: false,
      disableTimeOut: false,
      preventDuplicates: true,
      includeTitleDuplicates: true,
      enableHtml: true,
    }),
    MonacoEditorModule.forRoot(),
    CoreModule,
    SharedModule,
    NgIdleKeepaliveModule.forRoot(),
    EditorModule,
    IntercomModule.forRoot({ updateOnRouterChange: true }),
    ChannelsChatModule
  ],
  providers: [
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appConfigInitializerFn,
      multi: true,
      deps: [AppConfigService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appTranslateInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptorService,
      multi: true
    },
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation],
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: MessageService,
      useClass: KendoMessageService
    },
    {
      provide: ICON_SETTINGS,
      useValue: { type: 'font' }
    },
    {
      provide: IconSettingsService,
      useClass: IconService
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'channels', separator: '.', caseSensitive: true }),
      withLocalStorage(),
      withSessionStorage()
    ),
  ]
})
export class AppModule { }